/**
 * VideoManager handles all video-related operations including lazy loading,
 * preloading, and video lifecycle management
 */
export class VideoManager {
  constructor() {
    this.loadedVideos = new Map(); // Cache of loaded videos
    this.loadingPromises = new Map(); // Track ongoing loading operations
    this.preloadQueue = new Set(); // Queue of videos to preload
  }

  /**
   * Create video background element with lazy loading setup
   * @param {Object} bgConfig - Background configuration
   * @returns {HTMLElement} Video container element
   */
  createVideoBackground(bgConfig) {
    const container = document.createElement("div");
    container.className = "background-container";

    const video = document.createElement("video");
    video.className = "video-bg";
    video.muted = bgConfig.muted !== false;
    video.playsInline = true;

    if (bgConfig.autoplay) video.autoplay = true;
    if (bgConfig.loop) video.loop = true;

    // Store video config for lazy loading - don't set src immediately
    video.setAttribute("data-video-src", bgConfig.src);
    video.setAttribute("data-video-type", "video/mp4");

    // Create source element but don't set src yet (lazy loading)
    const source = document.createElement("source");
    source.type = "video/mp4";
    video.appendChild(source);

    container.appendChild(video);
    return container;
  }

  /**
   * Load video on demand with progress tracking and error handling
   * @param {number} stepId - Step ID for the video
   * @param {HTMLElement} stepElement - Step DOM element
   * @param {boolean} showProgress - Whether to show progress updates
   * @param {Function} updateProgressCallback - Callback for progress updates
   * @returns {Promise} Promise that resolves when video is loaded
   */
  async loadVideoOnDemand(
    stepId,
    stepElement,
    showProgress = false,
    updateProgressCallback = null,
  ) {
    const video = stepElement.querySelector("video[data-video-src]");
    if (!video) {
      console.log(`No lazy-loadable video found in step ${stepId}`);
      return Promise.resolve();
    }

    // Check if video is already loaded
    const source = video.querySelector("source");
    if (source.src) {
      console.log(`Video for step ${stepId} already loaded`);
      return Promise.resolve();
    }

    const videoSrc = video.getAttribute("data-video-src");
    const videoType = video.getAttribute("data-video-type");
    const videoFileName = videoSrc.split("/").pop();

    // Check if we're already loading this video
    const loadingKey = `${stepId}-${videoSrc}`;
    if (this.loadingPromises.has(loadingKey)) {
      console.log(`Video for step ${stepId} is already loading, waiting...`);
      return this.loadingPromises.get(loadingKey);
    }

    console.log(`Loading video on demand for step ${stepId}: ${videoFileName}`);

    if (showProgress && updateProgressCallback) {
      updateProgressCallback(95, `Loading video: ${videoFileName}...`);
    }

    const loadingPromise = new Promise((resolve, reject) => {
      let progressTimeout;

      // Set up loading event handlers
      const onCanPlay = () => {
        console.log(
          `Video loaded successfully for step ${stepId}: ${videoFileName}`,
        );
        this.cleanupEventListeners(video, onCanPlay, onError, onProgress);

        // Cache the loaded video
        this.loadedVideos.set(loadingKey, {
          stepId,
          videoSrc,
          loadedAt: Date.now(),
        });

        if (progressTimeout) clearTimeout(progressTimeout);
        this.loadingPromises.delete(loadingKey);
        resolve();
      };

      const onError = (error) => {
        console.error(`Failed to load video for step ${stepId}:`, error);
        this.cleanupEventListeners(video, onCanPlay, onError, onProgress);

        if (progressTimeout) clearTimeout(progressTimeout);
        this.loadingPromises.delete(loadingKey);
        reject(error);
      };

      const onProgress = () => {
        if (video.buffered.length > 0) {
          const buffered = video.buffered.end(0) / video.duration;
          const percent = Math.round(buffered * 100);
          console.log(`Video loading progress for step ${stepId}: ${percent}%`);
        }
      };

      // Add event listeners
      video.addEventListener("canplay", onCanPlay);
      video.addEventListener("error", onError);
      video.addEventListener("progress", onProgress);

      // Set timeout for loading (max 10 seconds)
      progressTimeout = setTimeout(() => {
        console.warn(
          `⏰ Video loading timeout for step ${stepId} after 10 seconds`,
        );
        this.cleanupEventListeners(video, onCanPlay, onError, onProgress);
        this.loadingPromises.delete(loadingKey);
        resolve(); // Continue anyway
      }, 10000);

      // Start loading the video
      source.src = videoSrc;
      source.type = videoType;
      video.load();
    });

    // Store the loading promise
    this.loadingPromises.set(loadingKey, loadingPromise);
    return loadingPromise;
  }

  /**
   * Preload video for next step in background
   * @param {number} currentStepId - Current step ID
   * @param {Map} stepsMap - Map of all steps
   * @returns {Promise} Promise that resolves when preloading is complete
   */
  async preloadNextStepVideo(currentStepId, stepsMap) {
    const currentStepData = stepsMap.get(currentStepId);
    if (!currentStepData?.config?.transitions?.next) {
      return;
    }

    const nextStepId = currentStepData.config.transitions.next;
    const nextStepData = stepsMap.get(nextStepId);

    if (!nextStepData) {
      console.warn(`Next step ${nextStepId} not found for preloading`);
      return;
    }

    console.log(`Preloading video for next step: ${nextStepId}`);

    try {
      await this.loadVideoOnDemand(nextStepId, nextStepData.element, false);
    } catch (error) {
      console.warn(`Failed to preload video for step ${nextStepId}:`, error);
    }
  }

  /**
   * Preload critical videos (first few steps)
   * @param {Array} criticalStepIds - Array of step IDs to preload
   * @param {Map} stepsMap - Map of all steps
   * @returns {Promise} Promise that resolves when all critical videos are loaded
   */
  async preloadCriticalVideos(criticalStepIds, stepsMap) {
    console.log(
      `Preloading critical videos for steps: ${criticalStepIds.join(", ")}`,
    );

    const preloadPromises = criticalStepIds.map((stepId) => {
      const stepData = stepsMap.get(stepId);
      if (stepData) {
        return this.loadVideoOnDemand(stepId, stepData.element, false);
      }
      return Promise.resolve();
    });

    try {
      await Promise.allSettled(preloadPromises);
      console.log(`Critical video preloading completed`);
    } catch (error) {
      console.warn(`Some critical videos failed to preload:`, error);
    }
  }

  /**
   * Clean up video event listeners
   * @private
   */
  cleanupEventListeners(video, onCanPlay, onError, onProgress) {
    video.removeEventListener("canplay", onCanPlay);
    video.removeEventListener("error", onError);
    video.removeEventListener("progress", onProgress);
  }

  /**
   * Clear video cache and free up memory
   * @param {number} maxAge - Maximum age in milliseconds (default: 5 minutes)
   */
  clearVideoCache(maxAge = 5 * 60 * 1000) {
    const now = Date.now();
    const toDelete = [];

    for (const [key, videoData] of this.loadedVideos.entries()) {
      if (now - videoData.loadedAt > maxAge) {
        toDelete.push(key);
      }
    }

    toDelete.forEach((key) => {
      this.loadedVideos.delete(key);
      console.log(`Cleared cached video: ${key}`);
    });

    if (toDelete.length > 0) {
      console.log(`Cleared ${toDelete.length} cached videos`);
    }
  }

  /**
   * Get video loading statistics
   * @returns {Object} Statistics about loaded videos
   */
  getVideoStats() {
    return {
      loadedCount: this.loadedVideos.size,
      loadingCount: this.loadingPromises.size,
      preloadQueueSize: this.preloadQueue.size,
      loadedVideos: Array.from(this.loadedVideos.keys()),
    };
  }

  /**
   * Reset video manager state
   */
  reset() {
    this.loadedVideos.clear();
    this.loadingPromises.clear();
    this.preloadQueue.clear();
  }
}

// Export singleton instance
export const videoManager = new VideoManager();
